import {
  Grid,
  Typography,
  Divider,
  Box,
  Paper,
  Button,
  CircularProgress,
  Chip,
  TextField,
  Autocomplete,
  Alert,
  createFilterOptions,
} from "@mui/material";
import { IconBrandTelegram, IconCopyPlusFilled } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import env from "react-dotenv";
import { useIntl } from "react-intl";

interface Proveedor {
  id_proveedor?: number;
  id?: number;
  razon_social: string;
  nombre_comercial: string;
  contacto?: string;
  correo?: string;
  telefono?: string;
}

interface ProveedorOpcion extends Proveedor {
  _id: number;
  _sugerido: boolean;
}

interface Detalles {
  id: number;
  id_requisicion: number;
  id_movimiento: number;
  id_inventario: number;
  cantidad: number;
  precio_unitario: number;
  precio_total: number;
  comentarios: string;
  created_at: Date;
  inventario: {
    id: number;
    nombre: string;
    clave: string;
  } | null;
}

interface Empresa {
  id: number;
  clave: string;
  razon_social: string;
  nombre: string;
  logo_empresa: string;
}

interface InfoComputoItem {
  id: number;
  id_equipo_adicional: number;
  tipo: string;
  cantidad: string;
  descripcion: string;
  programas: string;
  otro_programas: string;
  comprar: boolean;
  nombre: string;
  estatus: number;
  estatus_txt: string;
  comentario: string;
  color: string;
  categoria: string;
  asignado: string;
}

interface InfoComputo {
  tipo: string;
  notas: string;
  justificacion: string;
  notas_dsdt: string;
  items: InfoComputoItem[];
}

interface Requisicion {
  id: number;
  folio: string;
  id_solicitud_computo?: number | null;
  nombre_creador: string;
  nombre_usuario: string;
  nombre_empresa: string;
  empresa: Empresa;
  id_proyecto: number;
  nombre_proyecto: string;
  computo: string;
  computo_tipo: string;
  estatus_requisicion: number;
  created_at: Date;
  detalles: Detalles[];
  ids_inventario: number[];
  info_computo?: InfoComputo | null;
}

interface ProductoSeleccionado {
  proveedores: number[];
  cantidad: number;
  maximos?: number;
  minimos?: number;
  precio_unitario: number;
  precio_total: number;
}

interface CotizacionModalProps {
  verCotizacion?: Requisicion | null;
  procesandoCotizacion?: boolean;
  usuarioArjion?: string | null;
  idUsuario?: number;
  nombreCompletoAutorizando?: string;
  proveedores?: Proveedor[];
  getProveedores?: () => Promise<void>;
  proveedoresPorProducto?: Record<
    string,
    { nombre_producto?: string; proveedores: Proveedor[] } | Proveedor[]
  >;
  procesandoProveedores: boolean;
  getProveedoresPorProductos?: (ids: number[]) => Promise<void>;
  onCambioCotizacion?: (datos: any | null, listo: boolean) => void;
}

const idDeProveedor = (p: Proveedor) => Number(p.id_proveedor ?? p.id);

const etiquetaProveedor = (p: Proveedor) =>
  p.nombre_comercial || p.razon_social || `Proveedor ${idDeProveedor(p)}`;

const filtrarProveedores = createFilterOptions<ProveedorOpcion>({
  stringify: (p) =>
    `${p.nombre_comercial ?? ""} ${p.razon_social ?? ""} ${p.contacto ?? ""} ${p.correo ?? ""}`,
  ignoreAccents: true,
  ignoreCase: true,
});

const formatoMoneda = (valor: number | string | undefined) =>
  `$${Number(valor ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;

const CotizacionModal = ({
  verCotizacion,
  procesandoCotizacion,
  idUsuario,
  proveedores = [],
  getProveedores,
  proveedoresPorProducto = {},
  procesandoProveedores,
  getProveedoresPorProductos,
  onCambioCotizacion,
}: CotizacionModalProps) => {
  const intl = useIntl();

  const [seleccionados, setSeleccionados] = useState<Record<string, ProductoSeleccionado>>({});
  const [seleccionadosComputo, setSeleccionadosComputo] = useState<Record<string, number[]>>({});
  const [observaciones, setObservaciones] = useState<string>("");
  const [proveedoresGlobales, setProveedoresGlobales] = useState<ProveedorOpcion[]>([]);

  const esComputo = !!verCotizacion?.id_solicitud_computo;
  const infoComputo = verCotizacion?.info_computo ?? null;

  const idsInventario: number[] =
    !esComputo && Array.isArray(verCotizacion?.ids_inventario)
      ? verCotizacion!.ids_inventario
      : [];

  useEffect(() => {
    if (!verCotizacion) return;
    if (proveedores.length === 0) getProveedores?.();
    if (idsInventario.length > 0) getProveedoresPorProductos?.(idsInventario);
    setSeleccionados({});
    setSeleccionadosComputo({});
    setProveedoresGlobales([]);
  }, [verCotizacion?.id]);

  const itemsComputo = useMemo(
    () =>
      esComputo
        ? (infoComputo?.items ?? []).map((item, index) => ({
          ...item,
          _key: `computo-${item.id_equipo_adicional ?? "x"}-${index}`,
        }))
        : [],
    [esComputo, infoComputo?.items]
  );

  const detallePorInventario = useMemo(() => {
    const map = new Map<string, Detalles>();
    (verCotizacion?.detalles ?? []).forEach((d) => {
      const k = String(d.id_inventario);
      if (!map.has(k)) map.set(k, d);
    });
    return map;
  }, [verCotizacion?.detalles]);

  const productos = useMemo(() => {
    if (esComputo) return [];
    const permitidos = idsInventario.length > 0 ? new Set(idsInventario.map(String)) : null;
    return Array.from(detallePorInventario.keys()).filter((k) => !permitidos || permitidos.has(k));
  }, [esComputo, detallePorInventario, idsInventario]);

  const sugeridosPorProducto = useMemo(() => {
    const res: Record<string, Proveedor[]> = {};
    Object.entries(proveedoresPorProducto).forEach(([id, data]) => {
      res[id] = Array.isArray(data) ? data : data?.proveedores ?? [];
    });
    return res;
  }, [proveedoresPorProducto]);

  const catalogo = useMemo(() => {
    const map = new Map<number, Proveedor>();
    proveedores.forEach((p) => {
      const id = idDeProveedor(p);
      if (!Number.isNaN(id)) map.set(id, p);
    });
    Object.values(sugeridosPorProducto).forEach((lista) =>
      lista.forEach((p) => {
        const id = idDeProveedor(p);
        if (!Number.isNaN(id) && !map.has(id)) map.set(id, p);
      })
    );
    return map;
  }, [proveedores, sugeridosPorProducto]);

  const opcionesGlobales = useMemo<ProveedorOpcion[]>(
    () =>
      Array.from(catalogo.entries())
        .map(([id, p]) => ({ ...p, _id: id, _sugerido: false }))
        .sort((a, b) => etiquetaProveedor(a).localeCompare(etiquetaProveedor(b), "es")),
    [catalogo]
  );

  const opcionesDe = (id_inventario?: string): ProveedorOpcion[] => {
    if (!id_inventario) return opcionesGlobales;
    const sugeridos = new Set((sugeridosPorProducto[id_inventario] ?? []).map(idDeProveedor));
    return opcionesGlobales
      .map((p) => ({ ...p, _sugerido: sugeridos.has(p._id) }))
      .sort((a, b) => Number(b._sugerido) - Number(a._sugerido));
  };

  const baseProducto = (id_inventario: string): ProductoSeleccionado => {
    const d = detallePorInventario.get(id_inventario);
    return {
      proveedores: [],
      cantidad: Number(d?.cantidad ?? 0),
      precio_unitario: Number(d?.precio_unitario ?? 0),
      precio_total: Number(d?.precio_total ?? 0),
    };
  };

  const setProveedoresProducto = (id_inventario: string, ids: number[]) => {
    setSeleccionados((prev) => ({
      ...prev,
      [id_inventario]: {
        ...(prev[id_inventario] ?? baseProducto(id_inventario)),
        proveedores: ids,
      },
    }));
  };

  const setProveedoresComputo = (key: string, ids: number[]) => {
    setSeleccionadosComputo((prev) => ({ ...prev, [key]: ids }));
  };

  const agregarATodos = () => {
    const ids = proveedoresGlobales.map((p) => p._id);
    if (ids.length === 0) return;
    const unir = (actuales: number[] = []) => Array.from(new Set([...actuales, ...ids]));

    if (productos.length > 0) {
      setSeleccionados((prev) => {
        const nuevo = { ...prev };
        productos.forEach((id) => {
          const actual = nuevo[id] ?? baseProducto(id);
          nuevo[id] = { ...actual, proveedores: unir(actual.proveedores) };
        });
        return nuevo;
      });
    }
    if (itemsComputo.length > 0) {
      setSeleccionadosComputo((prev) => {
        const nuevo = { ...prev };
        itemsComputo.forEach((item) => {
          nuevo[item._key] = unir(nuevo[item._key]);
        });
        return nuevo;
      });
    }
    setProveedoresGlobales([]);
  };

  const totalPorCotizar = productos.length + itemsComputo.length;

  const todoSeleccionado =
    totalPorCotizar > 0 &&
    productos.every((id) => (seleccionados[id]?.proveedores?.length ?? 0) > 0) &&
    itemsComputo.every((item) => (seleccionadosComputo[item._key]?.length ?? 0) > 0);

  if (!verCotizacion) return null;

  const nombreProducto = (id: string) =>
    detallePorInventario.get(id)?.inventario?.nombre ?? `Producto ID ${id}`;

  const nombreEquipo = (item: InfoComputoItem) =>
    (item.id_equipo_adicional ? item.tipo : item.nombre) || "Equipo de cómputo";

  const renderOpcion = (props: any, option: ProveedorOpcion, selected: boolean) => (
    <li
      {...props}
      key={option._id}
      style={{
        ...props.style,
        borderLeft: selected ? "2px solid #084d6e" : "2px solid transparent",
        fontWeight: selected ? 600 : 400,
      }}
    >
      <Box sx={{ textAlign: "left" }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: 14,
            fontWeight: selected ? 600 : 400,
          }}
        >
          {etiquetaProveedor(option)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {option.razon_social}
          {option.correo ? ` · ${option.correo}` : ""}
        </Typography>
      </Box>
    </li>
  );

  const renderTags = (value: ProveedorOpcion[], getTagProps: any) =>
    value.map((o, index) => (
      <Chip
        {...getTagProps({ index })}
        key={o._id}
        label={etiquetaProveedor(o)}
        size="small"
        sx={{
          backgroundColor: "#084d6e",
          color: "#fff",
          "& .MuiChip-deleteIcon": { color: "#fff" },
        }}
      />
    ));

  const renderSelectorProveedores = (
    opciones: ProveedorOpcion[],
    idsSel: number[],
    onChange: (ids: number[]) => void
  ) => {
    const haySugeridos = opciones.some((o) => o._sugerido);
    const valor = opciones.filter((o) => idsSel.includes(o._id));
    return (
      <Autocomplete
        multiple
        disableCloseOnSelect
        fullWidth
        sx={{
          mt: 1.5,
          "& .MuiOutlinedInput-root": {
            paddingTop: "8px",
            paddingBottom: "8px",
          },
          "& .MuiAutocomplete-input": {
            paddingTop: "4px !important",
            paddingBottom: "4px !important",
          },
        }}
        options={opciones}
        groupBy={
          haySugeridos
            ? (o) => (o._sugerido ? "Relacionados al producto" : "Todos los proveedores")
            : undefined
        }
        filterOptions={filtrarProveedores}
        getOptionLabel={etiquetaProveedor}
        isOptionEqualToValue={(a, b) => a._id === b._id}
        value={valor}
        onChange={(_, nuevos) => onChange(nuevos.map((n) => n._id))}
        renderOption={(props, option, { selected }) => renderOpcion(props, option, selected)}
        renderTags={renderTags}
        noOptionsText={intl.formatMessage({ id: "general_sin_registros" })}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Proveedores a cotizar"
            placeholder={valor.length ? "" : "Buscar proveedor..."}
          />
        )}
      />
    );
  };

  const renderCabecera = (
    titulo: string,
    chips: { label: string; bg: string }[],
    cantidadSeleccionados: number
  ) => (
    <Box
      sx={{
        backgroundColor: "#084d6e",
        px: 2,
        py: 1.5,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold" color="#fff" sx={{ fontSize: 14 }}>
        {titulo}
      </Typography>
      {chips.map((c) => (
        <Chip
          key={c.label}
          label={c.label}
          size="small"
          sx={{ backgroundColor: c.bg, color: "#fff", fontSize: "0.7rem" }}
        />
      ))}
      {cantidadSeleccionados > 0 && (
        <Chip
          label={`${cantidadSeleccionados} seleccionado${cantidadSeleccionados > 1 ? "s" : ""} ✓`}
          size="small"
          sx={{ backgroundColor: "#4caf50", color: "#fff", fontSize: "0.7rem", ml: "auto" }}
        />
      )}
    </Box>
  );

  const Dato = ({ etiqueta, valor }: { etiqueta: string; valor: React.ReactNode }) => (
    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
      <strong>{etiqueta}: </strong>
      {valor}
    </Typography>
  );

  const tarjetaSx = {
    border: "1px solid #e0e0e0",
    borderRadius: 2,
    overflow: "hidden",
    height: "100%",
  };

  const sinNadaQueCotizar = esComputo ? itemsComputo.length === 0 : productos.length === 0;

  const payloadCotizacion = useMemo(() => {
    if (!verCotizacion) return null;

    const proveedores_seleccionados = Object.fromEntries(
      productos
        .filter((id) => (seleccionados[id]?.proveedores?.length ?? 0) > 0)
        .map((id) => [id, seleccionados[id]])
    );

    const equipos_computo_seleccionados = itemsComputo
      .filter((item) => (seleccionadosComputo[item._key]?.length ?? 0) > 0)
      .map((item) => ({
        id: item.id,
        id_equipo_adicional: item.id_equipo_adicional,
        tipo: item.id_equipo_adicional ? item.tipo : "Equipo",
        nombre: item.nombre,
        descripcion: item.descripcion,
        categoria: item.categoria,
        cantidad: Number(item.cantidad || 1),
        proveedores: seleccionadosComputo[item._key],
      }));

    return {
      id_requisicion: verCotizacion.id,
      id_usuario: idUsuario,
      es_computo: esComputo,
      id_solicitud_computo: verCotizacion.id_solicitud_computo ?? null,
      proveedores_seleccionados,
      equipos_computo_seleccionados,
      observaciones,
    };
  }, [verCotizacion?.id, idUsuario, esComputo, productos, seleccionados, itemsComputo, seleccionadosComputo, observaciones]);

  useEffect(() => {
    onCambioCotizacion?.(payloadCotizacion, todoSeleccionado);
  }, [payloadCotizacion, todoSeleccionado, onCambioCotizacion]);

  return (
    <Box sx={{ px: 0, pb: 2, mt: 0 }}>
      <Grid container spacing={2}>
        {/* Info general */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom sx={{ color: "#004c93" }}>
              Información general
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {verCotizacion.empresa?.logo_empresa && (
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" color="text.secondary">
                    Logo empresa
                  </Typography>
                  <Box
                    component="img"
                    src={`${env.API_URL_ASSETS}logo_empresas/${verCotizacion.empresa.logo_empresa}`}
                    alt="Logo empresa"
                    sx={{ mt: 0.5, width: "auto", height: 40 }}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Creado por
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verCotizacion.nombre_creador}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Usuario solicitante
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verCotizacion.nombre_usuario}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Empresa
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verCotizacion.nombre_empresa ?? verCotizacion.empresa?.razon_social ?? "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Proyecto
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {verCotizacion.nombre_proyecto ?? "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color="text.secondary">
                  Fecha
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {new Date(verCotizacion.created_at).toLocaleDateString("es-MX")}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Información de la solicitud de cómputo */}
        {esComputo && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ color: "#004c93", textAlign: "center" }}>
                Equipo de cómputo {verCotizacion.computo_tipo === "adicional" ? "(adicional)" : ""}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {!infoComputo ? (
                <Alert severity="warning" sx={{ justifyContent: "center" }}>
                  No se pudo obtener la información de la solicitud de cómputo
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Notas
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {infoComputo.notas || "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Justificación
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {infoComputo.justificacion || "—"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      Notas DSDT
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {infoComputo.notas_dsdt || "—"}
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Paper>
          </Grid>
        )}

        {/* Observaciones */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography
              variant="h6"
              color="primary"
              gutterBottom
              sx={{ color: "#004c93", textAlign: "center" }}
            >
              Observaciones
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2} sx={{ display: "flex", justifyContent: "center" }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  id="observaciones"
                  fullWidth
                  label={`${intl.formatMessage({ id: "input_observaciones" })}`}
                  variant="standard"
                  name="observaciones"
                  value={observaciones || ""}
                  onChange={(e) => setObservaciones(e.target.value)}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Selección de proveedores */}
        <Grid item xs={12}>
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography
              variant="h6"
              color="primary"
              gutterBottom
              sx={{ color: "#004c93", textAlign: "center" }}
            >
              {esComputo
                ? "Seleccionar proveedores por equipo de cómputo"
                : "Seleccionar proveedores por producto"}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              gutterBottom
              sx={{ display: "block", textAlign: "center" }}
            >
              {esComputo
                ? "Elige uno o varios proveedores de tu catálogo para cotizar cada equipo."
                : "Elige uno o varios proveedores de tu catálogo para cotizar cada producto. Los relacionados al producto en inventario aparecen primero como sugerencia."}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {procesandoProveedores ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : sinNadaQueCotizar ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                {esComputo
                  ? "No hay equipos de cómputo por cotizar en esta solicitud."
                  : "No hay productos pendientes por cotizar en esta requisición."}
              </Typography>
            ) : opcionesGlobales.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                No hay proveedores registrados.
              </Typography>
            ) : (
              <>
                {totalPorCotizar > 1 && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      mb: 2,
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: "#f5f9fc",
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      fullWidth
                      options={opcionesGlobales}
                      filterOptions={filtrarProveedores}
                      getOptionLabel={etiquetaProveedor}
                      isOptionEqualToValue={(a, b) => a._id === b._id}
                      value={proveedoresGlobales}
                      onChange={(_, nuevos) => setProveedoresGlobales(nuevos)}
                      renderOption={(props, option, { selected }) =>
                        renderOpcion(props, option, selected)
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          paddingTop: "8px",
                          paddingBottom: "8px",
                        },
                        "& .MuiAutocomplete-input": {
                          paddingTop: "4px !important",
                          paddingBottom: "4px !important",
                        },
                      }}
                      renderTags={renderTags}
                      noOptionsText={intl.formatMessage({ id: "general_sin_registros" })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="outlined"
                          label={
                            esComputo
                              ? "Proveedores para todos los equipos"
                              : "Proveedores para todos los productos"
                          }
                          placeholder="Buscar proveedor..."
                        />
                      )}
                    />
                    <Button
                      variant="outlined"
                      disabled={proveedoresGlobales.length === 0}
                      endIcon={<IconCopyPlusFilled />}
                      onClick={agregarATodos}
                      sx={{
                        whiteSpace: "nowrap",
                        borderColor: "#041d45",
                        color: "#041d45",
                        "& .MuiOutlinedInput-root": {
                          paddingTop: "8px",
                          paddingBottom: "8px",
                        },
                        "&:hover": {
                          background: "#041d45 !important",
                          borderColor: "#041d45",
                          color: "#fff"
                        },
                      }}
                    >
                      Agregar a todos
                    </Button>
                  </Box>
                )}

                <Grid container spacing={2}>
                  {itemsComputo.map((item) => {
                    const idsSel = seleccionadosComputo[item._key] ?? [];
                    return (
                      <Grid item xs={12} md={6} lg={4} key={item._key}>
                        <Box sx={tarjetaSx}>
                          {renderCabecera(
                            nombreEquipo(item),
                            [
                              {
                                label: item.id_equipo_adicional ? "Adicional" : "Equipo",
                                bg: "rgba(255,255,255,0.2)",
                              },
                              { label: "Cómputo", bg: "#6a4fb3" },
                            ],
                            idsSel.length
                          )}
                          <Box sx={{ p: 2 }}>
                            {item.categoria && <Dato etiqueta="Categoría" valor={item.categoria} />}
                            {item.nombre && item.descripcion && (
                              <Dato etiqueta="Descripción" valor={item.descripcion} />
                            )}
                            <Dato etiqueta="Cantidad" valor={item.cantidad || 1} />
                            {(item.programas || item.otro_programas) && (
                              <Dato
                                etiqueta="Programas"
                                valor={[item.programas, item.otro_programas]
                                  .filter(Boolean)
                                  .join(", ")}
                              />
                            )}
                            {item.comentario && (
                              <Dato etiqueta="Comentario" valor={item.comentario} />
                            )}
                            {renderSelectorProveedores(opcionesDe(), idsSel, (ids) =>
                              setProveedoresComputo(item._key, ids)
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}

                  {productos.map((id_inventario) => {
                    const detalle = detallePorInventario.get(id_inventario);
                    const idsSel = seleccionados[id_inventario]?.proveedores ?? [];

                    return (
                      <Grid item xs={12} md={6} lg={4} key={id_inventario}>
                        <Box sx={tarjetaSx}>
                          {renderCabecera(
                            nombreProducto(id_inventario),
                            [
                              {
                                label: detalle?.inventario?.clave ?? "—",
                                bg: "rgba(255,255,255,0.2)",
                              },
                            ],
                            idsSel.length
                          )}
                          <Box sx={{ p: 2 }}>
                            <Dato etiqueta="Cantidad" valor={detalle?.cantidad ?? "—"} />
                            <Dato
                              etiqueta="Precio unitario"
                              valor={formatoMoneda(detalle?.precio_unitario)}
                            />
                            <Dato
                              etiqueta="Precio total"
                              valor={formatoMoneda(detalle?.precio_total)}
                            />
                            {renderSelectorProveedores(opcionesDe(id_inventario), idsSel, (ids) =>
                              setProveedoresProducto(id_inventario, ids)
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}
          </Paper>
        </Grid>

        {/* Resumen de selección */}
        {todoSeleccionado && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2, backgroundColor: "#e8f4fd" }}>
              <Typography variant="h6" color="primary" gutterBottom>
                Resumen de proveedores seleccionados
              </Typography>
              <Divider sx={{ mb: 1 }} />
              {[
                ...itemsComputo.map((item) => ({
                  key: item._key,
                  nombre: nombreEquipo(item),
                  ids: seleccionadosComputo[item._key] ?? [],
                })),
                ...productos.map((id) => ({
                  key: id,
                  nombre: nombreProducto(id),
                  ids: seleccionados[id]?.proveedores ?? [],
                })),
              ].map((fila) => (
                <Box key={fila.key} sx={{ py: 0.75 }}>
                  <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {fila.nombre}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {fila.ids.map((idProv) => {
                      const p = catalogo.get(idProv);
                      return (
                        <Chip
                          key={idProv}
                          label={p ? etiquetaProveedor(p) : `Proveedor ${idProv}`}
                          size="small"
                          sx={{ backgroundColor: "#084d6e", color: "#fff" }}
                        />
                      );
                    })}
                  </Box>
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ))}
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default CotizacionModal;
